import React, {useCallback, useEffect, useRef, useState} from 'react';
import MainLayout from '../../mainLayout';
import styled from 'styled-components/native';
import ChatComponent from '../../../components/chat';
import {Dimensions, Platform} from 'react-native';
import {RootState} from '../../../redux/store';
import {connect, useSelector} from 'react-redux';
import {useFocusEffect} from '@react-navigation/native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {
  getThreadMessages,
  getUserThreads,
  onStreamingMessageEndAnimation,
  resetClinicalCase,
  sendMessage,
} from './actions';
import useChatVoiceRecognition from '../../../utils/hooks/useChatVoiceRecognition';
import {ChatScreenRef} from '../../../interfaces/components/chatComponent';
import {ThreadItemType} from '../../../interfaces/api/qna';
import {ChatItemType} from '../../../interfaces/reducers/chat';
import Animated from 'react-native-reanimated';
import AndroidSoftInput from '../../../utils/androidSoftInput';
import {ClinicalCaseProps} from '../../../interfaces/screens/clinicalCase';
import {stopStreamingSSE} from '../../../api/content/qna';
import {sleep} from '../../../utils/helpers';
import {useCustomHeaderHeight} from '../../../utils/hooks/useHeaderHeight';
import {ChatType} from '../../../utils/enums';
import {updateConfig} from '../../auth/login/actions';
import {ChatCategory} from '../../../interfaces/api/auth/login';
import ChatHeader from '../../../components/chat/chatHeader';
import {getCategoriesFromTabs} from '../../../utils/getCategoriesFromTabs';
import {toggleSettingsModal} from '../../../connected-components/settingsModal/actions';
import {tr} from '../../../translations';
import ChatHistory from '../../../components/chat/chatHistory';
import {Space} from '../home';
import {LinearGradient} from 'react-native-linear-gradient';
import {WebChatContainer, WebSideMenuContainer} from '../chat';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {useTheme} from 'styled-components';
import {Theme} from '../../../theme/lightTheme';
import screens from '../../../config/screens';

const Container = styled(Animated.View)<{heightInset: number}>`
  flex-grow: 1;

  ${({heightInset}) =>
    Platform.OS === 'ios' &&
    `height: ${Dimensions.get('screen').height - (heightInset || 0)}px;`}
`;
/**
 * ClinicalCase screen component
 *
 * @component
 * @param {ClinicalCaseProps} props - The screen properties, including navigation for handling routing.
 * @returns {React.ReactElement} The rendered ClinicalCase screen component.
 */
const ClinicalCase: React.FC<ClinicalCaseProps> = ({
  clinicalCase,
  auth,
  route,
  navigation,
}) => {
  const {
    isLoading,
    data,
    isError,
    presentUsers,
    isMessageError,
    isMessageLoading,
    streamMessageLoadingId,
    status,
    threadId,
  } = clinicalCase || {};
  const theme = useTheme() as Theme;
  const {wdp, hdp, isLargeScreen} = useResponsiveDimensions();
  const {tabs_with_categories} = auth.config || {};
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {isQNA, historyItem, tab_id} = route?.params || {};

  // Extract categories for clinical cases tab
  const chat_categories = getCategoriesFromTabs(tabs_with_categories, 'clinical_cases');
  // Custom header height.
  const headerHeight = useCustomHeaderHeight();
  const {id: userId, first_name} = auth?.currentUser || {};
  const [question, setQuestion] = useState<string>('');
  const [files, setFiles] = useState<any[] | undefined>();
  const [isNewThread, setIsNewThread] = useState<boolean>(false);
  const [lastMessage, setLastMessage] = useState<any>({
    question: '',
    file: null,
  });
  const [isSideMenuVisible, setIsSideMenuVisible] = useState(
    Platform.OS === 'web' && isLargeScreen,
  );
  const sideMenuWidth =
    Platform.OS === 'web' && isLargeScreen ? Math.min(wdp(30), 350) : 0;

  const flatListRef = useRef<ChatScreenRef>(null);
  const {isRecording, startListening, stopListening} =
    useChatVoiceRecognition(setQuestion);

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'android') {
        AndroidSoftInput.setSoftInputMode('adjustResize');
      }

      return () => {
        if (Platform.OS === 'android') {
          AndroidSoftInput.setSoftInputMode('stateHidden|adjustPan');
        }
        resetThread();
        setQuestion('');
      };
    }, []),
  );

  /**
   * Handles sending a message. Sets the last message, sends it, and resets the input fields.
   */
  const onQuestionPressedHandler = async (item: ChatCategory) => {
    const {selectedCase} = useSelector((state: RootState) => state.chat);
    setLastMessage({question: selectedCase, file: files});
    setQuestion('');
    setFiles(undefined);
    await sendMessage({
      type: ChatType.ClinicalCase,
      question,
      question_id: item.id,
      scrollToEnd: () => flatListRef.current?.scrollToEnd(),
      tab_id: tab_id ? Number(tab_id) : undefined,
    });
    flatListRef.current?.scrollToOffset(0);
    if (isNewThread) {
      setIsNewThread(false);
    }
  };

  /**
   * Start Record Recognition.
   */
  const onRecordPressHandler = () => {
    if (isRecording) {
      stopListening();
    } else {
      startListening();
    }
  };

  /**
   * Tries to resend the last message in case of failure.
   */
  const onMessageTryAgainHandler = async () => {
    await sendMessage({
      type: ChatType.ClinicalCase,
      question: lastMessage?.text || '',
      tryAgain: true,
      scrollToEnd: () => flatListRef.current?.scrollToEnd(),
      tab_id: tab_id ? Number(tab_id) : undefined,
    });
    flatListRef.current?.scrollToOffset(0);
  };

  const resetThread = async () => {
    stopStreamingSSE();
    await sleep(100);
    resetClinicalCase();
    stopListening();
  };

  const onGoBackPress = () => {
    resetThread();
    navigation?.navigate(screens.content.homeTab);
  };

  const onCreateNewChatPress = () => {
    resetThread();
    // Stay on the same screen, just reset the chat
  };

  useEffect(() => {
    if (historyItem) {
      onHistoryItemPress(historyItem);
    }
  }, [historyItem]);

  const onHistoryItemPress = useCallback(async (item: ThreadItemType | ChatItemType) => {
    setIsNewThread(true);
    const chatId = item.model_id || item.id;
    await getThreadMessages(chatId, true);
    await sleep(300);
    flatListRef?.current?.scrollToEnd(false);
  }, []);

  const onRefresh = useCallback(() => {
    if (threadId) {
      getThreadMessages(threadId, true);
    } else {
      resetClinicalCase();
    }
  }, [threadId]);


  return (
    <MainLayout
      showOverlay={false}
      isCustomHeader
      enableScroll={false}
      noPadding
      noPaddingHeader>
      <ChatHeader
        onGoBackPress={onGoBackPress}
        onHistoryPress={() =>
          toggleSettingsModal({
            title: tr(`chat.chatHistory`),
            component: () => (
              <ChatHistory
                isQNA={false}
                onChatItemPressed={onHistoryItemPress}
                selectedChatId={threadId}
                onCreateNewChatPressed={onCreateNewChatPress}
                tab_id={tab_id || "clinical_case"}
              />
            ),
          })
        }
      />
      {Platform.OS === 'web' && isLargeScreen ? (
        <div style={{display: 'flex', width: '100%', height: '100%'}}>
          <WebChatContainer sideMenuWidth={sideMenuWidth} isSideMenuVisible={isSideMenuVisible}>
            {Platform.OS === 'web' ? (
              <LinearGradient
                colors={[
                  theme.chat.startLinearGradient +
                    (data.length > 0 ? '55' : ''),
                  theme.chat.endLinearGradient + (data.length > 0 ? '55' : ''),
                ]}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                <ChatComponent
                  //@ts-ignore
                  ref={flatListRef}
                  userId={userId + ''}
                  userName={first_name || ''}
                  data={data}
                  isWriting={status?.isTyping && userId !== status?.userId}
                  isLoading={isLoading && data.length === 0}
                  isLoadingMore={isLoading && data.length > 20}
                  isError={isError}
                  isMessageSendingError={isMessageError}
                  isMessageSendingLoading={isMessageLoading}
                  streamMessageLoadingId={streamMessageLoadingId}
                  onMessageTryAgain={onMessageTryAgainHandler}
                  onRefresh={onRefresh}
                  onStreamingMessageEndAnimation={
                    onStreamingMessageEndAnimation
                  }
                  onlineUsers={presentUsers}
                  onCheckAnswerPressed={onQuestionPressedHandler}
                  question={question}
                  setQuestion={setQuestion}
                  onRecordHandler={onRecordPressHandler}
                  isRecording={isRecording}
                  isErrorAnswer={isError}
                  introQuestions={chat_categories || []}
                  onIntroQuestionsRefreshPress={updateConfig}
                  // selectedRecommendationTab={recommendationTab}
                  // onRecommendationClosePress={() => {
                  //   setRecommendationTab(undefined);
                  // }}
                  isQNA={isQNA}
                  // onQuestionPress={item => setRecommendationTab(item)}
                />
              </LinearGradient>
            ) : (
              <LinearGradient
                style={{flex: 1}}
                colors={
                  data.length > 0
                    ? [
                        theme.chat.startLinearGradient + '55',
                        theme.chat.endLinearGradient + '55',
                      ]
                    : [
                        theme.chat.startLinearGradient,
                        theme.chat.endLinearGradient,
                      ]
                }>
                <ChatComponent
                  //@ts-ignore
                  ref={flatListRef}
                  userId={userId + ''}
                  userName={first_name || ''}
                  data={data}
                  isWriting={status?.isTyping && userId !== status?.userId}
                  isLoading={isLoading && data.length === 0}
                  isLoadingMore={isLoading && data.length > 20}
                  isError={isError}
                  isMessageSendingError={isMessageError}
                  isMessageSendingLoading={isMessageLoading}
                  streamMessageLoadingId={streamMessageLoadingId}
                  onMessageTryAgain={onMessageTryAgainHandler}
                  onRefresh={onRefresh}
                  onStreamingMessageEndAnimation={
                    onStreamingMessageEndAnimation
                  }
                  onlineUsers={presentUsers}
                  onCheckAnswerPressed={onQuestionPressedHandler}
                  question={question}
                  setQuestion={setQuestion}
                  onRecordHandler={onRecordPressHandler}
                  isRecording={isRecording}
                  isErrorAnswer={isError}
                  introQuestions={chat_categories || []}
                  onIntroQuestionsRefreshPress={updateConfig}
                  isQNA={isQNA}
                />
              </LinearGradient>
            )}
          </WebChatContainer>
        </div>
      ) : (
        <Container style={{width: wdp(100)}} heightInset={headerHeight}>
          <ChatComponent
            //@ts-ignore
            ref={flatListRef}
            userId={userId + ''}
            userName={first_name || ''}
            data={data}
            isWriting={status?.isTyping && userId !== status?.userId}
            isLoading={isLoading && data.length === 0}
            isLoadingMore={isLoading && data.length > 20}
            isError={isError}
            isMessageSendingError={isMessageError}
            isMessageSendingLoading={isMessageLoading}
            streamMessageLoadingId={streamMessageLoadingId}
            onMessageTryAgain={onMessageTryAgainHandler}
            onRefresh={onRefresh}
            onStreamingMessageEndAnimation={onStreamingMessageEndAnimation}
            onlineUsers={presentUsers}
            onCheckAnswerPressed={onQuestionPressedHandler}
            question={question}
            setQuestion={setQuestion}
            onRecordHandler={onRecordPressHandler}
            isRecording={isRecording}
            isErrorAnswer={isError}
            introQuestions={chat_categories || []}
            onIntroQuestionsRefreshPress={updateConfig}
            isQNA={isQNA}
            onQuestionPress={onQuestionPressedHandler}
            paddingBottom={Math.max(bottomInset, hdp(5))}
          />
          <Space bottomInset={bottomInset / 2} />
        </Container>
      )}
    </MainLayout>
  );
};

const mapStateToProps = (state: RootState) => ({
  clinicalCase: state.clinicalCase,
  auth: state.auth,
});
export default connect(mapStateToProps, null)(ClinicalCase);
